/**
 * ProgressView - Map (left) + operator filter & table & store list (right).
 * Shows all operators' progress by default; filter by operator to narrow.
 */

import React, { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { TrackerLayout } from "./TrackerLayout";
import { TaskSubmissionNotes } from "./TaskSubmissionNotes";
import { getJson } from "@/lib/http";
import { trackerApiQueryString } from "@/lib/trackerTaskDefinitionFilter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    MapContainer,
    Polyline,
    TileLayer,
    CircleMarker,
    Popup,
    useMap,
} from "react-leaflet";
import L from "leaflet";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import type {
    TrackerCycleLiveSummaryResponse,
    TrackerOperatorLiveResponse,
    TrackerRouteSummaryItem,
    TrackerTask,
    TrackerOperatorState,
} from "@shared/tracker-types";
import { Eye, Camera, X, Trash2, Package, Loader2 } from "lucide-react";
import { TrackerMapLegend } from "./TrackerMapLegend";
import { FormatStatusTable } from "@/components/shared/FormatStatusTable";
import { formatTrackerVisitDateTime } from "./dateFormatting";
import {
    getOperationalPinColor,
    getOperationalPinStroke,
    OperationalStatusBadge,
    RejectionIndicator,
} from "./operationalStatusUi";

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

const DEFAULT_MAP_CENTER: [number, number] = [53.35, -6.26];
const POLL_INTERVAL_ACTIVE = 30000;
const POLL_INTERVAL_BACKGROUND = 120000;

type RouteItemWithOperator = TrackerRouteSummaryItem & { operatorId: number; operatorName: string };

function FitBounds({ points }: { points: [number, number][] }) {
    const map = useMap();
    useEffect(() => {
        if (!points.length) return;
        const bounds = L.latLngBounds(points);
        map.fitBounds(bounds, { padding: [40, 40] });
    }, [map, points]);
    return null;
}

function getStateBadge(state: TrackerOperatorState) {
    switch (state) {
        case "ACTIVE": return <Badge className="bg-green-500 hover:bg-green-600 text-xs">Active</Badge>;
        case "IDLE": return <Badge variant="outline" className="text-orange-500 border-orange-200 bg-orange-50 text-xs">Idle</Badge>;
        case "OFFLINE": return <Badge variant="outline" className="text-gray-400 text-xs">Offline</Badge>;
    }
}

function getTaskStatusBadge(status: TrackerTask["status"]) {
    switch (status) {
        case "complete": return <Badge variant="success" className="text-xs">Complete</Badge>;
        case "partial": return <Badge variant="warning" className="text-xs">Partial</Badge>;
        case "issue": return <Badge variant="destructive" className="text-xs">Issue</Badge>;
        case "pending":
        default: return <Badge variant="outline" className="text-xs">Pending</Badge>;
    }
}

function TaskCard({ task, onPhotoClick }: { task: TrackerTask; onPhotoClick: (url: string) => void }) {
    const actionWord = task.taskType === "REMOVAL" ? "Removed" : "Delivered";
    const hasFormatBreakdown = task.mediaFormats && task.mediaFormats.length > 0;
    return (
        <div className="border rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
                <div className="font-medium text-sm flex items-center gap-2">
                    {task.taskType === "REMOVAL" ? (
                        <Trash2 className="h-3.5 w-3.5 text-orange-500" />
                    ) : (
                        <Package className="h-3.5 w-3.5 text-blue-500" />
                    )}
                    {task.campaignName}
                    {task.assignmentId && (
                        <span className="text-xs text-muted-foreground font-normal">#{task.assignmentId}</span>
                    )}
                </div>
                <div className="flex items-center gap-1.5">
                    {task.taskType === "REMOVAL" && (
                        task.basisUsed === "DELIVERED"
                            ? <Badge variant="outline" className="text-xs bg-violet-50 text-violet-700 border-violet-200">Confirmed delivery</Badge>
                            : <Badge variant="outline" className="text-xs text-muted-foreground">Planned</Badge>
                    )}
                    {getTaskStatusBadge(task.status)}
                </div>
            </div>
            {task.packName && (
                <div className="text-xs text-muted-foreground">Pack: {task.packName}</div>
            )}
            {hasFormatBreakdown ? (
                <FormatStatusTable formats={task.mediaFormats} deliveredLabel={actionWord} compact />
            ) : (
                <FormatStatusTable
                    formats={[{
                        formatId: task.taskId,
                        formatName: task.packName || task.campaignName,
                        planned: task.expectedQuantity,
                        delivered: task.completedQuantity,
                    }]}
                    deliveredLabel={actionWord}
                    showTotal={false}
                    compact
                />
            )}
            {task.photoCount > 0 && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Camera className="h-3 w-3" />
                    {task.photoCount} photo{task.photoCount !== 1 ? "s" : ""}
                </div>
            )}
            <TaskSubmissionNotes installerNote={task.installerNote} notes={task.notes} />
            {task.photos.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                    {task.photos.map((photo) => (
                        <button
                            key={photo.photoId}
                            type="button"
                            onClick={() => onPhotoClick(photo.fullUrl)}
                            className="w-16 h-16 rounded border overflow-hidden hover:ring-2 ring-primary transition-all"
                        >
                            <img
                                src={photo.thumbUrl}
                                alt="Evidence"
                                className="w-full h-full object-cover"
                                onError={(e) => { (e.target as HTMLImageElement).src = photo.fullUrl; }}
                            />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function ProgressView() {
    const searchParams = new URLSearchParams(window.location.search);
    const cycleId = searchParams.get("cycleId");
    const retailerId = searchParams.get("retailerId");
    const taskDefinitionIds = searchParams.get("taskDefinitionIds");

    const [selectedOperatorId, setSelectedOperatorId] = useState<number | null>(null);
    const [selectedStore, setSelectedStore] = useState<RouteItemWithOperator | null>(null);
    const [lightboxPhoto, setLightboxPhoto] = useState<string | null>(null);
    const detailPanelRef = React.useRef<HTMLDivElement>(null);

    const [isTabVisible, setIsTabVisible] = useState(!document.hidden);
    useEffect(() => {
        const handleVisibility = () => setIsTabVisible(!document.hidden);
        document.addEventListener("visibilitychange", handleVisibility);
        return () => document.removeEventListener("visibilitychange", handleVisibility);
    }, []);

    const pollInterval = isTabVisible ? POLL_INTERVAL_ACTIVE : POLL_INTERVAL_BACKGROUND;

    const { data: liveSummary, isLoading, isRefetching, refetch, dataUpdatedAt } = useQuery<TrackerCycleLiveSummaryResponse | null>({
        queryKey: ["tracker-cycle-live-summary", cycleId, taskDefinitionIds],
        queryFn: async () => {
            if (!cycleId) return null;
            return await getJson<TrackerCycleLiveSummaryResponse>(
                `/api/analytics/tracker/live/${cycleId}/summary${trackerApiQueryString(taskDefinitionIds)}`,
            );
        },
        enabled: !!cycleId,
        refetchInterval: pollInterval,
        staleTime: 20000,
    });

    const operatorLiveEntries = liveSummary?.operators ?? [];
    const operators = operatorLiveEntries.map((entry) => entry.operator);

    const { data: operatorDetail, isLoading: detailLoading } = useQuery<TrackerOperatorLiveResponse | null>({
        queryKey: ["tracker-operator-live-detail", cycleId, selectedStore?.operatorId, taskDefinitionIds],
        queryFn: async () => {
            if (!cycleId || !selectedStore) return null;
            return await getJson<TrackerOperatorLiveResponse>(
                `/api/analytics/tracker/operator/${cycleId}/${selectedStore.operatorId}/live${trackerApiQueryString(taskDefinitionIds)}`,
            );
        },
        enabled: !!cycleId && !!selectedStore,
        staleTime: 30000,
    });

    const selectedDetail = useMemo(() => {
        if (!selectedStore || !operatorDetail) return null;
        return operatorDetail.routeItems.find((item) => item.storeId === selectedStore.storeId) ?? null;
    }, [selectedStore, operatorDetail]);

    const allRouteItems = useMemo((): RouteItemWithOperator[] => {
        const items: RouteItemWithOperator[] = [];
        operatorLiveEntries.forEach((entry) => {
            entry.routeItems.forEach((item) => {
                items.push({
                    ...item,
                    operatorId: entry.operator.operatorId,
                    operatorName: entry.operator.operatorName,
                });
            });
        });
        return items;
    }, [operatorLiveEntries]);

    const filteredRouteItems = useMemo(() => {
        if (selectedOperatorId == null) return allRouteItems;
        return allRouteItems.filter((r) => r.operatorId === selectedOperatorId);
    }, [allRouteItems, selectedOperatorId]);

    const orderedRoute = useMemo(() => {
        return [...filteredRouteItems].sort((a, b) => {
            if (a.sequence !== null && b.sequence !== null) return a.sequence - b.sequence;
            const tsA = a.checkInAt ? new Date(a.checkInAt).getTime() : 0;
            const tsB = b.checkInAt ? new Date(b.checkInAt).getTime() : 0;
            return tsA - tsB;
        });
    }, [filteredRouteItems]);

    // Normalize lat/lng to numbers (API or JSON may return strings); only include valid coords
    const hasValidCoords = (item: RouteItemWithOperator) => {
        const lat = Number(item.lat);
        const lng = Number(item.lng);
        return !Number.isNaN(lat) && !Number.isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
    };
    const routePoints = useMemo(() => {
        return orderedRoute
            .filter(hasValidCoords)
            .map((item) => [Number(item.lat), Number(item.lng)] as [number, number]);
    }, [orderedRoute]);

    const cycleQueryObj = new URLSearchParams();
    if (retailerId) cycleQueryObj.set("retailerId", retailerId);
    if (cycleId) cycleQueryObj.set("cycleId", cycleId);
    const cycleQuery = cycleQueryObj.toString() ? `?${cycleQueryObj.toString()}` : "";

    const lastUpdated = dataUpdatedAt ? new Date(dataUpdatedAt).toISOString() : null;

    const handleSelectStore = (item: RouteItemWithOperator) => {
        setSelectedStore(item);
        setTimeout(() => {
            detailPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
    };

    return (
        <TrackerLayout
            title="Progress"
            lastUpdated={lastUpdated}
            onRefresh={() => refetch()}
            isRefreshing={isRefetching}
        >
            {!cycleId ? (
                <div className="flex h-full items-center justify-center text-muted-foreground">
                    Select a cycle to view progress
                </div>
            ) : isLoading && allRouteItems.length === 0 ? (
                <div className="flex h-full items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
            ) : (
                <>
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 h-[calc(100vh-12rem)] min-h-[500px]">
                    {/* Left: Map (2 cols) */}
                    <Card className="xl:col-span-2 flex flex-col min-h-0">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base">Route Map</CardTitle>
                            <div className="pt-2">
                                <TrackerMapLegend />
                            </div>
                        </CardHeader>
                        <CardContent className="flex-1 min-h-0">
                            {routePoints.length === 0 ? (
                                <div className="h-full min-h-[300px] flex items-center justify-center bg-gray-50 rounded-lg border">
                                    <p className="text-muted-foreground text-center">
                                        {allRouteItems.length === 0
                                            ? "No route data for this cycle."
                                            : "No store coordinates available for the selected filter."}
                                    </p>
                                </div>
                            ) : (
                                <div className="h-full min-h-[300px] overflow-hidden rounded-lg border">
                                    <MapContainer
                                        center={routePoints[0] ?? DEFAULT_MAP_CENTER}
                                        zoom={10}
                                        className="h-full w-full"
                                        scrollWheelZoom
                                    >
                                        <TileLayer
                                            attribution="&copy; OpenStreetMap contributors"
                                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                        />
                                        {routePoints.length > 0 && (
                                            <>
                                                <FitBounds points={routePoints} />
                                                {selectedOperatorId != null && routePoints.length > 1 && (
                                                    <Polyline positions={routePoints} color="#2563eb" weight={3} opacity={0.6} />
                                                )}
                                            </>
                                        )}
                                        {orderedRoute.map((item) => {
                                            if (!hasValidCoords(item)) return null;
                                            const lat = Number(item.lat);
                                            const lng = Number(item.lng);
                                            const isSelected =
                                                selectedStore?.routeItemId === item.routeItemId
                                                && selectedStore?.operatorId === item.operatorId;
                                            const pinStroke = getOperationalPinStroke(
                                                item.operationalStatus,
                                                item.rejectedCount,
                                                isSelected,
                                            );
                                            return (
                                                <CircleMarker
                                                    key={`${item.operatorId}-${item.routeItemId}`}
                                                    center={[lat, lng]}
                                                    radius={10}
                                                    fillColor={getOperationalPinColor(item.operationalStatus)}
                                                    fillOpacity={0.9}
                                                    color={pinStroke.color}
                                                    weight={pinStroke.weight}
                                                    eventHandlers={{ click: () => handleSelectStore(item) }}
                                                >
                                                    <Popup>
                                                        <div className="space-y-1 min-w-[180px]">
                                                            <div className="font-semibold">{item.storeName}</div>
                                                            <div className="text-xs text-gray-500">{item.operatorName}</div>
                                                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                                <OperationalStatusBadge
                                                                    status={item.operationalStatus}
                                                                    rejectedCount={item.rejectedCount}
                                                                />
                                                                <RejectionIndicator count={item.rejectedCount} />
                                                            </div>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="w-full mt-2"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleSelectStore(item);
                                                                }}
                                                            >
                                                                View Details
                                                            </Button>
                                                        </div>
                                                    </Popup>
                                                </CircleMarker>
                                            );
                                        })}
                                    </MapContainer>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Right: Operator filter + table + store list (1 col) */}
                    <div className="flex flex-col gap-4 min-h-0 overflow-hidden">
                        <Card className="flex-shrink-0">
                            <CardHeader className="py-3">
                                <CardTitle className="text-sm">Operator</CardTitle>
                            </CardHeader>
                            <CardContent className="pt-0">
                                <Select
                                    value={selectedOperatorId == null ? "all" : String(selectedOperatorId)}
                                    onValueChange={(v) => setSelectedOperatorId(v === "all" ? null : parseInt(v, 10))}
                                >
                                    <SelectTrigger className="h-9">
                                        <SelectValue placeholder="All operators" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All operators</SelectItem>
                                        {operators.map((op) => (
                                            <SelectItem key={op.operatorId} value={String(op.operatorId)}>
                                                {op.operatorName}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </CardContent>
                        </Card>

                        <Card className="flex-1 min-h-0 flex flex-col overflow-hidden">
                            <CardHeader className="py-3">
                                <CardTitle className="text-sm">Operators ({operators.length})</CardTitle>
                            </CardHeader>
                            <CardContent className="pt-0 flex-1 min-h-0 overflow-auto">
                                    <div className="space-y-1">
                                    {operators.map((op) => {
                                        const opData = operatorLiveEntries.find((entry) => entry.operator.operatorId === op.operatorId);
                                        const storeCount = opData?.routeItems.length ?? 0;
                                        const doneCount = opData?.routeItems.filter((r) => r.operationalStatus === "COMPLETE").length ?? 0;
                                        return (
                                            <div
                                                key={op.operatorId}
                                                className={`flex items-center justify-between gap-2 rounded-md border px-2 py-1.5 text-sm ${
                                                    selectedOperatorId === op.operatorId ? "bg-primary/10 border-primary" : ""
                                                }`}
                                            >
                                                <div className="min-w-0 flex-1">
                                                    <div className="font-medium truncate">{op.operatorName}</div>
                                                    <div className="text-xs text-muted-foreground">
                                                        {doneCount}/{storeCount} stores
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1 flex-shrink-0">
                                                    {getStateBadge(op.state)}
                                                    <Link href={`/admin/activation-cockpit/operator/${op.operatorId}${cycleQuery}`}>
                                                        <Button variant="ghost" size="sm" className="h-7 px-2">
                                                            <Eye className="h-3.5 w-3.5" />
                                                        </Button>
                                                    </Link>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="flex-1 min-h-0 flex flex-col overflow-hidden">
                            <CardHeader className="py-3">
                                <CardTitle className="text-sm">Stores ({orderedRoute.length})</CardTitle>
                            </CardHeader>
                            <CardContent className="pt-0 flex-1 min-h-0 overflow-auto">
                                <div className="space-y-1">
                                    {orderedRoute.map((item) => (
                                        <button
                                            key={`${item.operatorId}-${item.routeItemId}`}
                                            type="button"
                                            onClick={() => handleSelectStore(item)}
                                            className={`w-full text-left rounded-lg border px-3 py-2 hover:bg-muted transition-colors text-sm ${
                                                selectedStore?.routeItemId === item.routeItemId && selectedStore?.operatorId === item.operatorId
                                                    ? "ring-2 ring-primary bg-muted"
                                                    : ""
                                            }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <span
                                                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                                    style={{ backgroundColor: getOperationalPinColor(item.operationalStatus) }}
                                                />
                                                <div className="min-w-0 flex-1">
                                                    <div className="font-medium truncate">{item.storeName}</div>
                                                    <div className="text-xs text-muted-foreground truncate">{item.operatorName}</div>
                                                </div>
                                                <div className="flex flex-col items-end gap-1">
                                                    <OperationalStatusBadge status={item.operationalStatus} />
                                                    <RejectionIndicator count={item.rejectedCount} />
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                    {orderedRoute.length === 0 && (
                                        <div className="text-sm text-muted-foreground text-center py-4">
                                            No stores to show. Select an operator or view route from the list.
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>

                {/* Store detail: campaigns (postings/removals) for selected store */}
                {selectedStore && (
                    <Card ref={detailPanelRef} className="mt-4">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base">{selectedStore.storeName}</CardTitle>
                            <div className="flex items-center gap-3 mt-1 flex-wrap">
                                <OperationalStatusBadge status={selectedStore.operationalStatus} />
                                <RejectionIndicator count={selectedStore.rejectedCount} />
                                <span className="text-sm text-muted-foreground">{selectedStore.operatorName}</span>
                                {selectedStore.storeAddress && (
                                    <span className="text-sm text-muted-foreground">{selectedStore.storeAddress}</span>
                                )}
                                <div className="text-xs text-muted-foreground ml-auto">
                                    {selectedStore.checkInAt && (
                                        <span>In: {formatTrackerVisitDateTime(selectedStore.checkInAt)}</span>
                                    )}
                                    {selectedStore.checkOutAt && (
                                        <span className="ml-2">Out: {formatTrackerVisitDateTime(selectedStore.checkOutAt)}</span>
                                    )}
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {detailLoading && (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Loading campaign details…
                                </div>
                            )}
                            {!detailLoading && !selectedDetail && (
                                <div className="text-sm text-muted-foreground py-4">
                                    No detailed task data available for this store.
                                </div>
                            )}
                            {!detailLoading && selectedDetail && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div className="space-y-3">
                                    <div className="text-sm font-medium flex items-center gap-2 border-b pb-2">
                                        <Package className="h-4 w-4 text-blue-500" />
                                        Postings ({selectedDetail.postings.length})
                                    </div>
                                    {selectedDetail.postings.length > 0 ? (
                                        <div className="space-y-3 max-h-[400px] overflow-auto">
                                            {selectedDetail.postings.map((task, idx) => (
                                                <TaskCard key={idx} task={task} onPhotoClick={setLightboxPhoto} />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-sm text-muted-foreground italic py-4">No postings</div>
                                    )}
                                </div>
                                <div className="space-y-3">
                                    <div className="text-sm font-medium flex items-center gap-2 border-b pb-2">
                                        <Trash2 className="h-4 w-4 text-orange-500" />
                                        Removals ({selectedDetail.removals?.length ?? 0})
                                    </div>
                                    {selectedDetail.removals && selectedDetail.removals.length > 0 ? (
                                        <div className="space-y-3 max-h-[400px] overflow-auto">
                                            {selectedDetail.removals.map((task, idx) => (
                                                <TaskCard key={idx} task={task} onPhotoClick={setLightboxPhoto} />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-sm text-muted-foreground italic py-4">No removals</div>
                                    )}
                                </div>
                            </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Lightbox for photos */}
                {lightboxPhoto && (
                    <div
                        className="fixed inset-0 z-[1000] bg-black/90 flex items-center justify-center p-4"
                        onClick={() => setLightboxPhoto(null)}
                    >
                        <button
                            type="button"
                            className="absolute top-4 right-4 text-white hover:text-gray-300 p-2"
                            onClick={() => setLightboxPhoto(null)}
                            title="Close lightbox"
                            aria-label="Close lightbox"
                        >
                            <X className="h-8 w-8" />
                        </button>
                        <img
                            src={lightboxPhoto}
                            alt="Evidence full size"
                            className="max-w-full max-h-full object-contain rounded-lg"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                )}
                </>
            )}
        </TrackerLayout>
    );
}
